import axios from "axios";

import { apiConfig } from "../config/authConfig";
import { CredentialManager } from "../classes/credentialManager";

import { IAddress } from "../types/base";
import { ICustomer, ICustomerCategory, ICustomerContact, ICustomerDetails, ICustomerExportRequest, ICustomerLight, ICustomerLocation, ICustomerMassClosedThroughUpdateRequest, ICustomerOperations } from "../types/customer";
import { ISelectOption } from "../types/list";
import { DocumentService } from "./documentService";
import { IFuzzySearchRequest } from "../types/system";

export namespace CustomerService {
	const credentialManager = CredentialManager.getInstance();

	/**********
	 * Convert one api response object to one Customer.
	 * @param data Customer response data from an API.
	 **********/
	export function mapCustomer(data: any) {
		let customer: ICustomer = {
			brand: data.brand,
			categories: data.categories,
			locations: data.locations,
			contact: data.contact,
			details: data.details,
			operations: data.operations,
			documents: DocumentService.mapDocuments(data.documents),
			id: data.id.toString(),
			mailingAddress: data.mailingAddress,
			shippingAddress: data.shippingAddress,
		}

		return customer;
	}

	/**********
	 * Convert an array of api response objects to an array of Customers.
	 * @param data Customer data from an API response.
	 **********/
	export function mapCustomers(data: any) {
		let customers: ICustomer[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				customers.push(mapCustomer(item));
			})
		}

		return customers;
	}

	/**********
	 * Convert one api response object to one Customer.
	 * @param data Customer response data from an API.
	 **********/
	export function mapCustomerLight(data: any) {
		let customer: ICustomerLight = {
			id: data.id,
			name: data.name,
			brand: data.brand,
			brandId: data.brandId,
			state: data.state,
			city: data.city,
			status: data.status,
			closedThroughDate: data.closedThroughDate
		}

		return customer;
	}

	/**********
	 * Convert an array of api response objects to an array of Customers.
	  * @param data Customer data from an API response.
	  **********/
	export function mapCustomerLightList(data: any) {
		let customers: ICustomerLight[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				customers.push(mapCustomerLight(item));
			})
		}

		return customers;
	}

	/**********
	 * Gets all customers.
	 **********/
	export async function getCustomers() {
		const apiResponse = axios.get(
			apiConfig.default + "/customer/all",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets all active customers.
	 **********/
	export async function getActiveCustomers() {
		const apiResponse = axios.get(
			apiConfig.default + "/customer/all/active",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets all active customers in the form of select options.
	 **********/
	export async function getActiveCustomerOptions() {
		const apiResponse = axios.get(
			apiConfig.default + "/customer/all/options",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets all active customers in the form of select options by employee.
	 * @param employeeId The employee's id.
	 **********/
	export async function getActiveCustomerOptionsByEmployee(employeeId: string) {

		const apiResponse = axios.get(
			apiConfig.default + "/customer/all/options" + (employeeId ? "/" + employeeId : ''),
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets all customers.
	 **********/
	export async function getCustomersByEmployee() {
		const apiResponse = axios.get(
			apiConfig.default + "/customer/all",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets one customer by id.
	 * @param customerId The id of the customer to get.
	 **********/
	export async function getCustomer(customerId: string) {
		const apiResponse = axios.get(
			apiConfig.default + "/customer/" + customerId,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Add a new customer starting with details.
	 * @param details The customer's details.
	 **********/
	export async function addCustomer(details: ICustomerDetails) {
		const apiResponse = axios.post(
			apiConfig.default + "/customer",
			details,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Update a customer's details.
	 * @param customerId The customer's id.
	 * @param details The customer's details.
	 **********/
	export async function setDetails(customerId: string, details: ICustomerDetails) {
		const apiResponse = axios.put(
			apiConfig.default + "/customer/" + customerId + "/details",
			details,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Update a customer's addresses.
	 * @param customerId The customer's id.
	 * @param physicalAddress The customer's physical address.
	 * @param mailingAddress The customer's mailing address.
	 **********/
	export async function setAddresses(customerId: string, physicalAddress: IAddress, mailingAddress: IAddress) {
		let payload = {
			"physicalAddress": physicalAddress,
			"mailingAddress": mailingAddress
		}

		const apiResponse = axios.put(
			apiConfig.default + "/customer/" + customerId + "/addresses",
			payload,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Update a customer's contact.
	 * @param customerId The customer's id.
	 * @param contact The customer's contact.
	 **********/
	export async function setContact(customerId: string, contact: ICustomerContact) {
		const apiResponse = axios.put(
			apiConfig.default + "/customer/" + customerId + "/contact",
			contact,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Add a customer category.
	 * @param customerId The customer's id.
	 * @param category The category.
	 **********/
	export async function addCategory(customerId: string, category: ICustomerCategory) {
		const apiResponse = axios.put(
			apiConfig.default + "/customer/category",
			category,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Remove a customer category.
	 * @param customerId The customer's id.
	 * @param categoryId The category id.
	 **********/
	export async function deactivateCategory(categoryId: number) {
		const apiResponse = axios.put(
			apiConfig.default + "/customer/category/" + categoryId + "/deactivate",
			"mock payload",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}


	/**********
	 * Convert one api response object to one Customer.
	 * @param data Customer response data from an API.
	 **********/
	export function mapCustomerCategory(data: any) {
		let customer: ICustomerCategory = {
			id: data.id,
			name: data.name,
			customerId: data.customerId,
			vendorId: data.vendorId,
		}

		return customer;
	}

	/**********
	 * Convert an array of api response objects to an array of Customers.
	 * @param data Customer data from an API response.
	 **********/
	export function mapCustomerCategories(data: any) {
		let customers: ICustomerCategory[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				customers.push(mapCustomerCategory(item));
			})
		}

		return customers;
	}

	/**********
	 * Gets all customer categories.
	 **********/
	export async function getCustomerCategories() {
		const apiResponse = axios.get(
			apiConfig.default + "/customercategory/all",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets customer categories for the specified customer ID.
	 **********/
	export async function getCustomerCategoriesByCustomer(customerId: number) {
		const apiResponse = axios.get(
			apiConfig.default + "/customercategory/customer/" + customerId,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets customer categories for the specified customer ID and vendor ID combination.
	 **********/
	export async function getCustomerCategoriesByCustomerAndVendor(customerId: number, vendorId: number) {
		const apiResponse = axios.get(
			apiConfig.default + "/customercategory/customer/" + customerId + "/vendor/" + vendorId,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Add a customer Location.
	 * @param location The location.
	 **********/
	export async function addLocation(location: ICustomerLocation) {
		const apiResponse = axios.put(
			apiConfig.default + "/customer/location",
			location,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Remove a customer location.
	 * @param locationId The location id.
	 **********/
	export async function deactivateLocation(locationId: number) {
		const apiResponse = axios.put(
			apiConfig.default + "/customer/location/" + locationId + "/deactivate",
			"mock payload",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}


	/**********
	 * Convert one api response object to one CustomerLocation.
	 * @param data CustomerLocation response data from an API.
	 **********/
	export function mapCustomerLocation(data: any) {
		let location: ICustomerLocation = {
			id: data.id,
			customerId: data.customerId,
			customerName: data.customerName,
			locationName: data.locationName,
			isActive: data.isActive,
		}

		return location;
	}

	/**********
	 * Convert an array of api response objects to an array of CustomerLocations.
	 * @param data CustomerLocation data from an API response.
	 **********/
	export function mapCustomerLocations(data: any) {
		let locations: ICustomerLocation[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				locations.push(mapCustomerLocation(item));
			})
		}

		return locations;
	}

	/**********
	 * Convert an array of api response objects to an array of ISelectOptions for customer locations.
	 * @param data CustomerLocation data from an API response.
	 **********/
	export function mapCustomerLocationOptions(data: any): ISelectOption[] {
		let locations: ISelectOption[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				locations.push({
					name: item.locationName,
					value: item.id.toString()
				} as ISelectOption);
			})
		}

		return locations;
	}

	/**********
	 * Gets customer locations for the specified customer ID.
	 **********/
	export async function getCustomerLocationsByCustomerId(customerId: number) {
		const apiResponse = axios.get(
			apiConfig.default + "/customerlocation/customer/" + customerId,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets customer locations for the specified customer ID.
	 **********/
	export async function getActiveCustomerLocationsByCustomerId(customerId: number) {
		const apiResponse = axios.get(
			apiConfig.default + "/customerlocation/customer/" + customerId + "/active",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets a customer operations item for the specified customer ID.
	 **********/
	export async function getCustomerOperationsByCustomerId(customerId: number) {
		const apiResponse = axios.get(
			apiConfig.default + "/customer/operations/customer/" + customerId,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets a customer operations item by its ID.
	 **********/
	export async function getCustomerOperations(customerOperationsId: number) {
		const apiResponse = axios.get(
			apiConfig.default + "/customer/operations/" + customerOperationsId,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Sets a customer operations item.
	 **********/
	export async function setCustomerOperations(request: ICustomerOperations) {
		const apiResponse = axios.post(
			apiConfig.default + "/customer/operations",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Sets a customer operations item identified by its assigned customer's ID.
	 **********/
	export async function setCustomerOperationsByCustomerId(request: ICustomerOperations) {
		const apiResponse = axios.post(
			apiConfig.default + "/customer/operations/customer",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Deletes a customer operations item.
	 **********/
	export async function deleteCustomerOperations(customerOperationsId: number) {
		const apiResponse = axios.delete(
			apiConfig.default + "/customer/operations/" + customerOperationsId,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
     * Search for duplicate existing company names using a fuzzy compare method.
     **********/
	export async function fuzzySearchForDuplicateCompanyNames(request: IFuzzySearchRequest) {
		const apiResponse = axios.post(
			apiConfig.default + "/customer/fuzzy/companyname",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
     * Mass update the closed-through date for multiple customers.
     **********/
	export async function massUpdateClosedThroughDate(request: ICustomerMassClosedThroughUpdateRequest) {
		const apiResponse = axios.post(
			apiConfig.default + "/customer/closing",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Exports the entire filtered customer list to an Excel document.
	 * @param request The export request parameters containing filter and sort options.
	 **********/
	export async function exportCustomerListToExcel(request: ICustomerExportRequest) {
		const apiResponse = axios.post(
			apiConfig.default + `/customer/export`,
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}
}